import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  PersonAdd,
  Business,
  Description,
  CheckCircle,
  Email,
  Schedule,
  SecurityIcon,
  AdminPanelSettings
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const RequestAccess = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [requestId, setRequestId] = useState(null);
  
  const [formData, setFormData] = useState({
    // Personal Information
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    
    // Professional Information
    company: '',
    department: '',
    job_title: '',
    phone: '',
    country: '',
    
    // Access Request Details
    reason_for_access: '',
    intended_use_case: '',
    expected_project_count: 1,
    referral_source: ''
  });

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Spain',
    'Italy', 'Netherlands', 'Australia', 'Japan', 'India', 'Singapore',
    'Brazil', 'Mexico', 'Other'
  ];

  const referralSources = [
    'Search Engine (Google, etc.)',
    'Social Media',
    'Colleague Recommendation',
    'Online Article/Blog',
    'Conference/Event',
    'Partner Company',
    'Other'
  ];

  const steps = [
    {
      label: 'Personal Information',
      description: 'Basic account details',
      icon: <PersonAdd />
    },
    {
      label: 'Professional Details',
      description: 'Work and contact information',
      icon: <Business />
    },
    {
      label: 'Access Request',
      description: 'Why you need access to Ailyce',
      icon: <Description />
    },
    {
      label: 'Review & Submit',
      description: 'Confirm your information',
      icon: <CheckCircle />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Personal Information
        if (!formData.name.trim()) return 'Name is required';
        if (!formData.email.trim()) return 'Email is required';
        if (!formData.password) return 'Password is required';
        if (formData.password.length < 8) return 'Password must be at least 8 characters';
        if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
        break;
      
      case 1: // Professional Details
        // Optional step - no validation required
        break;
      
      case 2: // Access Request
        if (!formData.reason_for_access.trim()) return 'Reason for access is required';
        if (formData.reason_for_access.trim().length < 20) return 'Please provide a more detailed reason (minimum 20 characters)';
        break;
      
      default:
        break;
    }
    return null;
  };

  const handleNext = () => {
    const validation = validateStep(activeStep);
    if (validation) {
      setError(validation);
      return;
    }
    
    setError(null);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Final validation
      for (let i = 0; i < 3; i++) {
        const validation = validateStep(i);
        if (validation) {
          setError(validation);
          setActiveStep(i);
          return;
        }
      }

      const response = await fetch('/api/auth/register-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...formData,
          referral_source: formData.referral_source || undefined
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(data);
        setRequestId(data.data.request_id);
        setActiveStep(4); // Move to success step
      } else {
        throw new Error(data.detail || data.message || 'Failed to submit request');
      }

    } catch (err) {
      setError(err.message || 'Failed to submit registration request');
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalInformation = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Full Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          required
          placeholder="Enter your full name"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          type="email"
          label="Email Address"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          required
          placeholder="Enter your email address"
          helperText="This will be your login email"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="password"
          label="Password"
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          required
          placeholder="Create a password"
          helperText="Minimum 8 characters"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="password"
          label="Confirm Password"
          value={formData.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
          required
          placeholder="Confirm your password"
        />
      </Grid>
    </Grid>
  );

  const renderProfessionalDetails = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Company"
          value={formData.company}
          onChange={(e) => handleInputChange('company', e.target.value)}
          placeholder="Your company name"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Department"
          value={formData.department}
          onChange={(e) => handleInputChange('department', e.target.value)}
          placeholder="Your department"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Job Title"
          value={formData.job_title}
          onChange={(e) => handleInputChange('job_title', e.target.value)}
          placeholder="Your job title"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          placeholder="Your phone number"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Country</InputLabel>
          <Select
            value={formData.country}
            label="Country"
            onChange={(e) => handleInputChange('country', e.target.value)}
          >
            {countries.map((country) => (
              <MenuItem key={country} value={country}>
                {country}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>How did you hear about us?</InputLabel>
          <Select
            value={formData.referral_source}
            label="How did you hear about us?"
            onChange={(e) => handleInputChange('referral_source', e.target.value)}
          >
            {referralSources.map((source) => (
              <MenuItem key={source} value={source}>
                {source}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderAccessRequest = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Reason for Access"
          value={formData.reason_for_access}
          onChange={(e) => handleInputChange('reason_for_access', e.target.value)}
          required
          placeholder="Please provide a detailed explanation of why you need access to Ailyce. Include your use case, goals, and how you plan to use the platform."
          helperText={`${formData.reason_for_access.length}/1000 characters (minimum 20 required)`}
          error={formData.reason_for_access.length > 0 && formData.reason_for_access.length < 20}
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Intended Use Case"
          value={formData.intended_use_case}
          onChange={(e) => handleInputChange('intended_use_case', e.target.value)}
          placeholder="Optional: Describe specific projects or scenarios where you'll use Ailyce"
          helperText="This helps us understand your needs and provide better support"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Expected Number of Projects"
          value={formData.expected_project_count}
          onChange={(e) => handleInputChange('expected_project_count', parseInt(e.target.value) || 1)}
          inputProps={{ min: 1, max: 1000 }}
          helperText="Estimated number of projects you plan to create"
        />
      </Grid>
    </Grid>
  );

  const renderReviewSubmit = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Your Information
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary">Personal Information</Typography>
          <Typography><strong>Name:</strong> {formData.name}</Typography>
          <Typography><strong>Email:</strong> {formData.email}</Typography>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary">Professional Details</Typography>
          <Typography><strong>Company:</strong> {formData.company || 'Not provided'}</Typography>
          <Typography><strong>Job Title:</strong> {formData.job_title || 'Not provided'}</Typography>
          <Typography><strong>Country:</strong> {formData.country || 'Not provided'}</Typography>
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">Access Request</Typography>
          <Typography><strong>Reason:</strong> {formData.reason_for_access}</Typography>
          <Typography><strong>Expected Projects:</strong> {formData.expected_project_count}</Typography>
        </Grid>
      </Grid>
      
      <Box mt={3}>
        <Alert severity="info">
          <Typography variant="body2">
            By submitting this request, you agree to our terms of service and privacy policy. 
            Your request will be reviewed by our admin team, and you'll receive an email notification 
            with the decision (typically within 1-3 business days).
          </Typography>
        </Alert>
      </Box>
    </Box>
  );

  const renderSuccess = () => (
    <Box textAlign="center">
      <CheckCircle 
        sx={{ 
          fontSize: 80, 
          color: theme.palette.success.main, 
          mb: 2 
        }} 
      />
      
      <Typography variant="h5" gutterBottom>
        Request Submitted Successfully!
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Your registration request has been submitted for admin review.
      </Typography>
      
      <Card sx={{ mt: 3, bgcolor: theme.palette.background.default }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            What happens next?
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <Email color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Email Confirmation"
                secondary="You'll receive a confirmation email shortly"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <AdminPanelSettings color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Admin Review"
                secondary="Our team will review your request (1-3 business days)"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Schedule color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Decision Notification"
                secondary="You'll receive login credentials if approved"
              />
            </ListItem>
          </List>
          
          {requestId && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Request ID:</strong> {requestId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Save this ID to check your request status
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Box mt={3}>
        <Button
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{ mr: 2 }}
        >
          Back to Login
        </Button>
        
        <Button
          variant="outlined"
          onClick={() => navigate('/contact')}
        >
          Contact Support
        </Button>
      </Box>
    </Box>
  );

  if (success && activeStep === 4) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          p: 2
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            {renderSuccess()}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 4,
        px: 2
      }}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h3" component="h1" gutterBottom>
            Request Additional Access
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Submit a request for additional permissions or access levels. This request will be reviewed by administrators.
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={
                      index === 1 ? (
                        <Typography variant="caption">Optional</Typography>
                      ) : null
                    }
                    icon={step.icon}
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {step.description}
                    </Typography>
                    
                    {index === 0 && renderPersonalInformation()}
                    {index === 1 && renderProfessionalDetails()}
                    {index === 2 && renderAccessRequest()}
                    {index === 3 && renderReviewSubmit()}
                    
                    {error && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                      </Alert>
                    )}
                    
                    <Box sx={{ mb: 2, mt: 3 }}>
                      <div>
                        {index === 3 ? (
                          <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                          >
                            {loading ? 'Submitting...' : 'Submit Request'}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={handleNext}
                          >
                            Continue
                          </Button>
                        )}
                        
                        <Button
                          disabled={index === 0}
                          onClick={handleBack}
                          sx={{ ml: 1 }}
                        >
                          Back
                        </Button>
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
        
        <Box textAlign="center" mt={3}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Link to="/login" style={{ color: theme.palette.primary.main }}>
              Sign in here
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RequestAccess;